import { Component, OnInit, inject, signal, HostListener, LOCALE_ID, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductVTO } from '../../../pages/admin-dashboard/products/product.model';
import { CommonModule } from '@angular/common';
import { ProductService } from '../../../services/product/product.service';
import { ProductInfo } from './product-info/product-info';
import { ProductSubProducts } from './product-sub-products/product-sub-products';
import { ProductCurrentStock } from './product-current-stock/product-current-stock';
import { ProductHistoryStock } from './product-history-stock/product-history-stock';
import { ProductsFileMgt } from '../../../services/product/file-management/products.file-mgt.service';
import { SafeUrl } from '@angular/platform-browser';
import { finalize, forkJoin } from 'rxjs';
import { ImageCarousel } from './utils/image-carousel/image-carousel';
import { ErrorData } from '../../../utils/error-data/error-data';
import { ProductAttributesComponent } from './product-attributes-component/product-attributes-component';
import { ConfirmDeleteModal } from '../add-product-component/steps/attributes-step/confirm-delete-modal/confirm-delete-modal';
import { Error } from '../../public-user-component/auth/shared/error/error';
import { SuccessfulNotification } from '../../../utils/successful-notification/successful-notification';
import { Loading } from '../../../utils/loading/loading';
import { SCOPES } from '../../../services/scope/scope-constants';
import { HasScopeDirective } from '../../../services/scope/has-scope.directive';
import { ConfirmationDialog } from '../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { ProductConfiguration } from './product-configuration/product-configuration';

@Component({
  selector: 'app-product-details',
  standalone: true,
  imports: [
    CommonModule,
    ProductInfo,
    ProductSubProducts,
    ProductCurrentStock,
    ProductHistoryStock,
    ImageCarousel,
    ErrorData,
    ProductAttributesComponent,
    ConfirmDeleteModal,
    Error,
    SuccessfulNotification,
    Loading,
    HasScopeDirective,
    ConfirmationDialog,
    ProductConfiguration
],
  templateUrl: './product-details.html',
  styleUrl: './product-details.scss',
})
export class ProductDetails implements OnInit {
  readonly SCOPES = SCOPES;
  private productService = inject(ProductService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fileMgt = inject(ProductsFileMgt);
  private locale = inject(LOCALE_ID);

  product = signal<ProductVTO | null>(null);
  productImages = signal<{ url: SafeUrl | string; isImage: boolean }[]>([]);
  isLoading = signal<boolean>(true);
  productType = signal<'has-sub' | 'has-no-sub' | null>(null);
  activeTab = signal<'details' | 'attributes' | 'sub-products' | 'current-stock' | 'history-stock' | 'configuration'>(
    'details',
  );
  isDropdownOpen = signal<boolean>(false);
  productImageUrl = signal<{ url: SafeUrl | string; isImage: boolean } | undefined>(undefined);
  errorLoadingProduct = signal<string>('');
  showDeleteModal = signal<boolean>(false);
  apiError = signal<string>('');
  showSuccess = signal<boolean>(false);
  isActivated = computed(() => this.product()?.isActive);
  confirmationActivationModal = signal<boolean>(false);
  activationType = signal<'deactivate' | 'activate' | null>(null);
  loadingActivation = signal<boolean>(false);
  errorActivation = signal<string>('');
  isArabic = computed(() => this.locale.startsWith('ar'));
  notificationSuccess = signal<boolean>(false);
  successMessage = signal<string>('');
  ngOnInit() {
    const productId = this.route.snapshot.params['id'];
    if (productId) {
      this.loadProduct(+productId);
    }
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state || history.state;

    if (state && state['deletedType']) {
      const type = state['deletedType'];

      if (type === 'sub-product') {
        const msg = this.isArabic() ? 'تم حذف الصنف الفرعي بنجاح.' : 'Sub-product deleted successfully.';
        this.successMessage.set(msg);
      } else {
        const msg = this.isArabic() ? 'تم حذف الصنف بنجاح.' : 'Product deleted successfully.';
        this.successMessage.set(msg);
      }

      this.showSuccess.set(true);
      setTimeout(() => {
        this.showSuccess.set(false);
      }, 2000);
    }
  }
  loadProduct(id: number) {
    this.isLoading.set(true);
    this.productService
      .getAdminProductById(id)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe({
        next: (data) => {
          this.product.set(data);
          this.productImageUrl.set({ url: '', isImage: false });
          this.isLoading.set(false);
          const idStr = data.id.toString();
          if (idStr.startsWith('2')) this.productType.set('has-sub');
          if (idStr.startsWith('1')) this.productType.set('has-no-sub');

          if (!data.profileImageFid) {
            this.productService.getAllAdminProducts({}, this.isArabic() ? 'ar' : 'en').subscribe((res) => {
              const found = res.list?.find((p) => p.id === id);
              if (found?.profileImageFid) {
                this.fetchImage(found.profileImageFid).subscribe({
                  next: (img) => this.productImageUrl.set(img),
                  error: () => this.productImageUrl.set({ url: '', isImage: false }),
                });
              } else {
                this.productImageUrl.set({ url: '', isImage: false });
              }
            });
          } else {
            this.fetchImage(data.profileImageFid).subscribe({
              next: (img) => this.productImageUrl.set(img),
              error: () => this.productImageUrl.set({ url: '', isImage: false }),
            });

            if (data.imagesFids && data.imagesFids.length > 1) {
              const imageRequests = data.imagesFids.slice(1).map((fid) => this.fetchImage(fid));
              forkJoin(imageRequests).subscribe({
                next: (images) => this.productImages.set(images),
                error: () => this.productImages.set([]),
              });
            }
          }
          this.isLoading.set(false);
        },
        error: (err) => {
          this.isLoading.set(false);
          this.productImageUrl.set({ url: '', isImage: false });
          this.errorLoadingProduct.set(
            this.isArabic() ? err.error.messageAr || 'حدث خطأ ما' : err.error.messageEn || 'Some error occurred!',
          );
        },
      });
  }

  private fetchImage(fid: string) {
    return this.fileMgt.getImageUrl(fid);
  }

  setActiveTab(tab: 'details' | 'attributes' | 'sub-products' | 'current-stock' | 'history-stock' | 'configuration') {
    this.activeTab.set(tab);
  }
  navigateToAddStock() {
    const p = this.product();
    if (p) this.router.navigate(['/admin/products/add-stock', p.id]);
  }

  handleDeleteAction() {
    const p = this.product();
    if (!p) return;

    // if (this.productType() === 'has-sub') {
    //   this.apiError.set(
    //     this.isArabic()
    //       ? 'لا يمكن حذف الصنف. هذا الصنف يحتوي على منتجات فرعية .'
    //       : 'Cannot delete product. This product has sub-products.',
    //   );
    //   return;
    // }

    // if (this.productType() === 'has-no-sub' && (p.availableQuantity ?? 0) > 0) {
    //   this.apiError.set(
    //     this.isArabic()
    //       ? 'لا يمكن حذف الصنف. هذا الصنف لا يزال يحتوي على مخزون.'
    //       : 'Cannot delete product. This product still has available stock.',
    //   );
    //   return;
    // }

    this.apiError.set('');
    this.showDeleteModal.set(true);
  }

  confirmDeletion() {
    const p = this.product();
    if (p) {
      this.productService.deleteProduct(p.id).subscribe({
        next: () => {
          this.showDeleteModal.set(false);

          setTimeout(() => {
            this.showSuccess.set(true);
            setTimeout(() => {
              this.showSuccess.set(false);
              this.router.navigate(['/admin/products']);
            }, 2000);
          }, 100);
        },
        error: (err) => {
          this.showDeleteModal.set(false);

          console.log('Error from backend:', err);
          const errorBody = err.error || err;

          const msgAr = errorBody?.messageAr || errorBody?.error?.messageAr;
          const msgEn = errorBody?.messageEn || errorBody?.error?.messageEn;

          const errorMessage = this.isArabic()
            ? msgAr || 'لا يمكن حذف هذا المنتج حالياً'
            : msgEn || 'Cannot delete this product right now';

          this.apiError.set(errorMessage);
        },
      });
    }
  }
  @HostListener('document:click')
  onDocumentClick() {
    this.isDropdownOpen.set(false);
  }

  toggleDropdown(event: Event) {
    event.stopPropagation();
    this.isDropdownOpen.update((v) => !v);
  }

  activateProduct() {
    const p = this.product();
    if (!p) return;
    this.loadingActivation.set(true);
    this.productService
      .activateProduct(p.id)
      .pipe(
        finalize(() => {
          this.loadingActivation.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.product.set({ ...p, isActive: true });
          this.confirmationActivationModal.set(false);
          this.notificationSuccess.set(true);
          setTimeout(() => {
            this.notificationSuccess.set(false);
            this.activationType.set(null);
          }, 2000);
        },
        error: (err) => {
          this.errorActivation.set(
            this.isArabic() ? err.messageAr || 'حدث بعض الخطأ!' : err.messageEn || 'some error occured!',
          );
        },
      });
  }

  deactivateProduct() {
    const p = this.product();
    if (!p) return;
    this.loadingActivation.set(true);

    this.productService
      .deactivateProduct(p.id)
      .pipe(
        finalize(() => {
          this.loadingActivation.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.product.set({ ...p, isActive: false });
          this.confirmationActivationModal.set(false);
          this.notificationSuccess.set(true);
          setTimeout(() => {
            this.notificationSuccess.set(false);
            this.activationType.set(null);
          }, 2000);
        },
        error: (err) => {
          this.errorActivation.set(
            this.isArabic() ? err.error.messageAr || 'حدث بعض الخطأ!' : err.error.messageEn || 'some error occured',
          );
        },
      });
  }

  handleActivationAction(action: 'Activate Product' | 'Deactivate Product') {
    this.confirmationActivationModal.set(true);
    this.activationType.set(action === 'Activate Product' ? 'activate' : 'deactivate');
  }

  handleAction(action: string) {
    const p = this.product();
    if (action === 'Add Stock') this.navigateToAddStock();
    else if (action === 'Edit Product' && p) this.router.navigate(['/admin/products/edit-product', p.id]);
    else if (action === 'Delete Product') this.handleDeleteAction();
    else if (action === 'Activate Product' || action === 'Deactivate Product') {
      this.handleActivationAction(action);
    }
    this.isDropdownOpen.set(false);
  }

  confirmationModalContent = computed(() => {
    const ar = this.isArabic();
    const type = this.activationType();
    const loading = this.loadingActivation();

    const content = {
      deactivate: {
        title: ar ? 'تأكيد إلغاء التفعيل' : 'Confirm Deactivation',
        description: ar
          ? 'هل أنت متأكد من رغبتك في إلغاء تفعيل هذا الصنف؟'
          : 'Are you sure you want to deactivate this product?',
        confirmContent: ar
          ? loading
            ? 'جارٍ إلغاء التفعيل...'
            : 'تأكيد إلغاء التفعيل'
          : loading
            ? 'Deactivating...'
            : 'Confirm Deactivation',
      },
      activate: {
        title: ar ? 'تأكيد تفعيل الصنف' : 'Confirm Product Activation',
        description: ar
          ? 'هل أنت متأكد من رغبتك في تفعيل هذا الصنف؟ سيصبح الصنف مرئياً للعملاء ومتاحاً للشراء'
          : 'Are you sure you want to activate this product? The product will become visible to customers and available for purchase.',
        confirmContent: ar
          ? loading
            ? 'جارٍ التفعيل...'
            : 'تأكيد التفعيل'
          : loading
            ? 'Activating...'
            : 'Confirm Activation',
      },
    };

    return content[type!];
  });
}
